<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">AfterDoc 마이그레이션</h1>
          </div>
          <span class="pageDesc">
            AfterDoc data-store.json 하나만 올리면 시스템이 알림톡(템플릿 / 시술 태그 / 시나리오)과 상담·고객(상담방 / 메시지 / 고객)
            대상을 자동으로 판별합니다. 발견된 대상마다 드라이런 후 이관할 수 있어, 파일을 두 번 올릴 필요가 없습니다.
          </span>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 영역 -->
    <div class="card-body px-0 pt-3 pb-2">
      <div class="card content-wrap-card">
        <!-- ===== 공용: 대상 병원 (이름으로 검색해서 고른다) ===== -->
        <div class="field-block">
          <label class="field-label">대상 병원 <span class="required-mark">*</span></label>
          <a-input-search
            v-model:value="companyKeyword"
            placeholder="병원명을 입력하고 검색하세요"
            enter-button="검색"
            :loading="companyLoading"
            style="width: 360px"
            @search="searchCompanies"
          />
          <!-- 검색 결과에서 병원을 클릭해 선택한다 -->
          <div v-if="companyResults.length" class="company-results">
            <div
              v-for="c in companyResults"
              :key="c._id"
              class="company-result-item"
              :class="{ selected: selectedCompanyId === c._id }"
              @click="selectCompany(c)"
            >
              {{ c.name }}
            </div>
          </div>
          <div v-if="selectedCompanyId" class="selected-company">
            선택된 병원: <strong>{{ selectedCompanyName }}</strong>
          </div>
          <p class="field-hint">병원명으로 검색해 목록에서 선택하세요. 선택한 병원으로 알림톡(crm-api)과 상담·고객(counsel-api) 이관이 함께 진행됩니다.</p>
        </div>

        <!-- ===== 공용: data-store.json 업로드 (한 번만) ===== -->
        <div class="field-block">
          <label class="field-label">data-store.json <span class="required-mark">*</span></label>
          <a-upload-dragger
            :before-upload="handleStoreSelect"
            :show-upload-list="false"
            accept=".json"
          >
            <p class="ant-upload-drag-icon"><InboxOutlined /></p>
            <p class="ant-upload-text">클릭하거나 파일을 드래그하여 업로드</p>
            <p class="ant-upload-hint">
              AfterDoc data-store.json (태그 / 주의사항 / 자동화 + 상담방 / 메시지 / 고객). 최대 ~81MB까지 지원합니다.
            </p>
          </a-upload-dragger>
          <div v-if="storeFile" class="selected-file">
            <FileTextOutlined />
            <span class="file-name">{{ storeFile.name }}</span>
            <a-button type="link" danger size="small" @click="clearStore">삭제</a-button>
          </div>
        </div>

        <a-spin
          :spinning="parsing"
          tip="브라우저에서 파일을 읽는 중입니다. 파일이 크면 잠시 걸릴 수 있습니다."
        >
          <template v-if="detected">
            <!-- ===== 발견된 대상 요약 스트립 (각 이관 섹션으로 바로가기) ===== -->
            <div class="detect-strip">
              <span class="detect-title">이 파일에서 발견된 이관 대상</span>
              <p v-if="hasAlimtalk && hasCounsel" class="detect-guide">
                아래에 <strong>알림톡 이관</strong>과 <strong>상담·고객 이관</strong> 두 가지가 모두 있습니다. 각각 독립적으로 진행하며, 둘 다 이관하려면 두 섹션을 모두 실행하세요.
              </p>
              <div class="detect-groups">
                <div v-if="hasAlimtalk" class="detect-group">
                  <span class="detect-label">알림톡</span>
                  <a-tag color="purple">자동화 시나리오 {{ detected.alimtalk.automations.toLocaleString() }}</a-tag>
                  <a-tag color="purple">주의사항 후보 {{ detected.alimtalk.cautions.toLocaleString() }}</a-tag>
                  <a-button type="link" size="small" @click="scrollToSection('alimtalk-section')">알림톡 이관으로 이동 ↓</a-button>
                </div>
                <div v-if="hasCounsel" class="detect-group">
                  <span class="detect-label">상담·고객</span>
                  <a-tag color="blue">고객 {{ detected.counsel.patients.toLocaleString() }}</a-tag>
                  <a-tag color="blue">상담방 {{ detected.counsel.rooms.toLocaleString() }}</a-tag>
                  <a-tag color="blue">메시지 {{ detected.counsel.messages.toLocaleString() }}</a-tag>
                  <a-button type="link" size="small" @click="scrollToSection('counsel-section')">상담·고객 이관으로 이동 ↓</a-button>
                </div>
              </div>
            </div>

            <a-alert
              v-if="!hasAlimtalk && !hasCounsel"
              type="warning"
              show-icon
              class="empty-alert"
              message="이관할 수 있는 대상이 없습니다"
              description="이 data-store.json 에는 알림톡(자동화/주의사항)도 상담·고객(상담방/메시지) 데이터도 없습니다. 올바른 내보내기 파일인지 확인해주세요."
            />

            <!-- ===== 알림톡 카드 ===== -->
            <section v-if="hasAlimtalk" id="alimtalk-section" class="target-card">
              <div class="target-head">
                <h4 class="target-title"><a-tag color="purple">1</a-tag> 알림톡 이관</h4>
                <span class="muted">템플릿 / 시술 태그 / 시나리오 → 서티라이프 CRM (crm-api)</span>
              </div>
              <AlimtalkMigrationCard
                :company-id="selectedCompanyId"
                :company-name="selectedCompanyName"
                :data-store="dataStore"
              />
            </section>

            <!-- 알림톡을 진행하다 보면 아래 상담 섹션을 놓치기 쉬워, 사이에 명확히 안내한다 -->
            <div
              v-if="hasAlimtalk && hasCounsel"
              class="between-notice"
              @click="scrollToSection('counsel-section')"
            >
              <ArrowDownOutlined />
              아래에 <strong>상담·고객 이관</strong>도 있습니다 — 고객 {{ detected.counsel.patients.toLocaleString() }}명 · 상담방 {{ detected.counsel.rooms.toLocaleString() }}개. 클릭하면 이동합니다.
            </div>

            <!-- ===== 상담·고객 카드 ===== -->
            <section v-if="hasCounsel" id="counsel-section" class="target-card">
              <div class="target-head">
                <h4 class="target-title"><a-tag color="blue">2</a-tag> 상담·고객 이관</h4>
                <span class="muted">상담방 / 메시지 / 고객 → counsel-api (서버측 파싱)</span>
              </div>
              <CounselMigrationPanel
                :company-id="selectedCompanyId"
                :company-name="selectedCompanyName"
                :file="storeFile"
                :detected="detected.counsel"
              />
            </section>
          </template>
        </a-spin>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { message } from "ant-design-vue";
import { InboxOutlined, FileTextOutlined, ArrowDownOutlined } from "@ant-design/icons-vue";
import apiInstance from "@/axios";
import AlimtalkMigrationCard from "./components/AlimtalkMigrationCard.vue";
import CounselMigrationPanel from "./components/CounselMigrationPanel.vue";
import { readJsonFile } from "./afterdocParser";
import { detectTargets, hasAlimtalkTargets, hasCounselTargets } from "./afterdocCounselParser";

export default {
  name: "AfterdocMigration",
  components: {
    InboxOutlined,
    FileTextOutlined,
    ArrowDownOutlined,
    AlimtalkMigrationCard,
    CounselMigrationPanel,
  },
  setup() {
    // 공용 대상 병원 — 병원이 많아(1000+) 선로딩은 초과분을 못 찾는다.
    // 이름을 입력하고 검색 버튼을 누르면 서버측 검색(inapi search=name)해서, 결과 목록에서 클릭해 고른다.
    const companyKeyword = ref("");
    const companyResults = ref([]); // 검색 결과
    const companyLoading = ref(false);
    const selectedCompanyId = ref(undefined);
    const selectedCompany = ref(null); // 선택된 병원 문서

    // 공용 파일 (원본 File + 파싱된 객체)
    const storeFile = ref(null);
    const dataStore = ref(null);
    const detected = ref(null);
    const parsing = ref(false);

    const selectedCompanyName = computed(() =>
      selectedCompany.value ? selectedCompany.value.name : "-"
    );

    const hasAlimtalk = computed(() => hasAlimtalkTargets(detected.value));
    const hasCounsel = computed(() => hasCounselTargets(detected.value));

    // 병원명 서버측 검색 (inapi). 검색 버튼(또는 엔터)으로만 조회한다.
    const searchCompanies = async () => {
      const keyword = companyKeyword.value.trim();
      if (!keyword) {
        companyResults.value = [];
        return;
      }
      companyLoading.value = true;
      try {
        const response = await apiInstance.post("/companies/list/v2", {
          page: 1,
          limit: 50,
          type: "HOSPITAL",
          search: "name",
          condition: keyword,
        });
        companyResults.value = (response.data && response.data.docs) || [];
        if (!companyResults.value.length) {
          message.info("검색 결과가 없습니다. 병원명을 확인해주세요.");
        }
      } catch (error) {
        console.error("병원 검색 실패:", error);
        message.error("병원 검색에 실패했습니다.");
      } finally {
        companyLoading.value = false;
      }
    };

    // 결과 목록에서 병원을 클릭해 선택한다.
    const selectCompany = (company) => {
      selectedCompanyId.value = company._id;
      selectedCompany.value = company;
    };

    // 발견 대상 섹션으로 부드럽게 스크롤한다(알림톡 진행 중 상담 섹션을 놓치지 않게).
    const scrollToSection = (id) => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const clearStore = () => {
      storeFile.value = null;
      dataStore.value = null;
      detected.value = null;
    };

    // 파일 하나로 두 대상을 모두 판별한다. 원본 File 은 상담 카드가 서버 업로드에 그대로 쓴다.
    const handleStoreSelect = (file) => {
      if (!file.name.match(/\.json$/i)) {
        message.warning("JSON 파일만 업로드 가능합니다.");
        return false;
      }
      storeFile.value = file;
      dataStore.value = null;
      detected.value = null;
      runDetect(file);
      return false; // 브라우저에서만 읽는다 — 자동 서버 업로드 막는다
    };

    const runDetect = async (file) => {
      parsing.value = true;
      try {
        const store = await readJsonFile(file);
        dataStore.value = store;
        detected.value = detectTargets(store);

        if (!hasAlimtalk.value && !hasCounsel.value) {
          message.warning("이관 가능한 알림톡 / 상담 대상을 찾지 못했습니다.");
          return;
        }
        message.success("파일을 읽었습니다. 발견된 대상 아래에서 이관을 진행하세요.");
      } catch (error) {
        console.error("파일 파싱 실패:", error);
        message.error(error.message || "파일을 읽는 중 오류가 발생했습니다.");
        clearStore();
      } finally {
        parsing.value = false;
      }
    };

    return {
      companyKeyword,
      companyResults,
      companyLoading,
      selectedCompanyId,
      selectedCompanyName,
      searchCompanies,
      selectCompany,
      scrollToSection,
      storeFile,
      dataStore,
      detected,
      parsing,
      hasAlimtalk,
      hasCounsel,
      handleStoreSelect,
      clearStore,
    };
  },
};
</script>

<style scoped>
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
}

.pageDesc {
  font-size: 14px;
  color: #6c757d;
  margin-top: 8px;
  display: block;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.content-wrap-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  padding: 24px;
}

.field-block {
  margin-bottom: 24px;
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.required-mark {
  color: #f5222d;
}

.field-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #999;
}

.selected-file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  margin-top: 12px;
}

.selected-file .file-name {
  flex: 1;
  font-weight: 500;
  color: #333;
}

.detect-strip {
  margin: 4px 0 20px;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
}

.detect-title {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}

.detect-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.detect-group {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.detect-label {
  font-weight: 600;
  color: #555;
  margin-right: 4px;
}

.empty-alert {
  margin-bottom: 16px;
}

.target-card {
  margin-top: 24px;
  padding: 20px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
}

.target-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.target-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.muted {
  color: #999;
}

/* 병원 검색 결과 목록 */
.company-results {
  margin-top: 8px;
  max-width: 360px;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}

.company-result-item {
  padding: 8px 12px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
}

.company-result-item:last-child {
  border-bottom: none;
}

.company-result-item:hover {
  background: #f5f5f5;
}

.company-result-item.selected {
  background: #fffbe6;
  font-weight: 600;
  color: #ad6800;
}

.selected-company {
  margin-top: 10px;
  padding: 8px 14px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  max-width: 360px;
}

/* 두 대상이 모두 있을 때, 위쪽 안내 문구 */
.detect-guide {
  margin: 0 0 12px;
  font-size: 13px;
  color: #555;
  line-height: 1.6;
}

/* 알림톡 카드와 상담 카드 사이 — 아래 상담 섹션을 놓치지 않게 하는 안내 배너 */
.between-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 14px 18px;
  background: #e6f4ff;
  border: 1px solid #91caff;
  border-radius: 8px;
  font-size: 14px;
  color: #0958d9;
  cursor: pointer;
}

.between-notice:hover {
  background: #d6ecff;
}
</style>
